import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiPhone, FiCalendar, FiMapPin, FiCheck } = FiIcons;

const Contact = () => {
  const contactInfo = [
    {
      icon: FiMail,
      title: "Email",
      value: "hello@wethebillions.com",
      href: "mailto:hello@wethebillions.com",
      color: "bg-accent-magenta/10 text-accent-magenta"
    },
    {
      icon: FiPhone,
      title: "Call/Text", 
      value: "(202) 656-0054",
      href: "tel:+12026560054",
      color: "bg-accent-gold/20 text-orange-600"
    },
    {
      icon: FiCalendar,
      title: "Book Online",
      value: "wethebillions.com",
      href: "https://wethebillions.neetocal.com/consultation",
      color: "bg-accent-lavender/20 text-purple-600"
    }
  ];

  const consultationBenefits = [
    "Personalized sleep assessment",
    "Executive function evaluation", 
    "Custom strategy recommendations",
    "No-pressure conversation"
  ];

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            Ready to Stop Counting Sheep?
          </h2>
          <p className="text-xl text-gray-600 font-raleway max-w-3xl mx-auto">
            Book your free consultation and discover how executive function training can transform your sleep and focus.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-6">
                Get in Touch
              </h3>
              <div className="grid gap-4">
                {contactInfo.map((info, index) => (
                  <motion.a
                    key={index}
                    href={info.href}
                    target={info.title === "Book Online" ? "_blank" : undefined}
                    rel={info.title === "Book Online" ? "noopener noreferrer" : undefined}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 group"
                  >
                    <div className={`p-3 rounded-full ${info.color} group-hover:scale-110 transition-transform duration-200`}>
                      <SafeIcon icon={info.icon} className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 font-raleway">{info.title}</div>
                      <div className="text-gray-600 font-raleway">{info.value}</div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h4 className="text-lg font-playfair font-bold text-gray-900 mb-4">
                Your Free Consultation Includes:
              </h4>
              <div className="space-y-3">
                {consultationBenefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3"
                  >
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700 font-raleway">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="bg-accent-light/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-accent-magenta" />
                <span className="font-semibold text-gray-900 font-raleway">Service Areas</span>
              </div>
              <p className="text-gray-700 font-raleway">
                Virtual (globally) & In-Person Sessions (MD & VA)
              </p>
            </div>
          </motion.div>

          {/* Booking Widget */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden"
          >
            <div className="p-6 bg-gradient-to-r from-accent-magenta to-accent-purple text-white">
              <h3 className="text-xl font-playfair font-bold mb-2">
                Book Your Free Consultation
              </h3>
              <p className="opacity-90 font-raleway">
                Choose a time that works for you
              </p>
            </div>
            <div className="p-1">
              <iframe
                allowFullScreen
                height="717"
                width="100%"
                frameBorder="0"
                title="Book Your Consultation - We The Billions"
                src="https://wethebillions.neetocal.com/embed/5f6e78fe-d1ce-43f6-b1ce-797e0cec501a"
                className="rounded-b-3xl"
              />
            </div>
          </motion.div>
        </div>

        {/* Backup Booking Link */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 font-raleway mb-4">
            Having trouble with the booking widget?
          </p>
          <a
            href="https://wethebillions.neetocal.com/consultation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-accent-magenta hover:text-accent-magenta/80 font-semibold transition-colors duration-200"
          >
            <SafeIcon icon={FiCalendar} className="w-5 h-5" />
            Click here to book directly
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;